import Vue from 'utils/vueUtils';
import { baseApi } from 'core/api';
import {
    useAuth2fStore,
    useClientsStore,
    useFilterSegmentStore,
    useNumberStore,
    useSubscriptionsStore,
    useToastStore,
} from 'store/modules';

const state = () => ({
    _organization: {},
    _settings: [],
    _feature_flags: {},
    _custom_fields_list: [],
    _broadcast_limits: {},
    _isLoadingMultiOrganizations: false,
    _multiOrganizationsList: [],
});

const getters = {
    featureFlags: (state) => state._feature_flags,
    organization: (state) => state._organization,
    organizationHipaaEnabled() {
        return Boolean(this.organization?.hipaa?.is_hipaa_compliant);
    },
    organizationHipaa: (state) => state._organization.hipaa,
    organizationSettings: (state) => state._settings,
    totalSentMessages: (state) => 0,
    totalReceivedMessages: (state) => 0,
    customFieldsList: (state) => state._custom_fields_list,
    broadcastLimits: (state) => state._broadcast_limits,
    isLoadingMultiOrganizations: (state) => state._isLoadingMultiOrganizations,
    multiOrganizationsList: (state) => state._multiOrganizationsList,
    isAgency: (state) => state._organization?.is_agency === true,
    isClient: (state) => Boolean(state._organization?.agency_id),
    isV2auth: (state) => true,
};

const actions = {
    updateOrganizationProfile(form) {
        return form.post('core/organization/profile').then((response) => {
            this._setOrganizationProfileUpdated(response.data);
            return response;
        });
    },

    accountBalanceUpdated(data) {
        this._setOrganizationAccountBalanceUpdated(data);
    },

    updateBillingCreditsMicroservice(params) {
        return baseApi.post('billing/credits', params).then((response) => {
            this._setOrganizationAccountBalanceUpdated(response.data);
            return response;
        });
    },

    resendPhoneVerification() {
        return baseApi.post('core/organization/otp-number/reapply').then((response) => {
            return response;
        });
    },

    upgradeBillingUpgrade(form) {
        return form.post('core/billing/upgrade').then((response) => {
            useSubscriptionsStore().subscriptionUpgraded(response.data);
            return response;
        });
    },

    _setSubscription(data) {
        this._organization.account_balance = data.account_balance;
        this._organization.account_credits = data.account_credits;
        this._organization.trial_credits = 0;
        this._organization.trial_ends_at = null;
    },

    getBillingPreviewPlanMicroservice(data) {
        return baseApi.get('billing/preview/plan', data).then((response) => {
            return response.data;
        });
    },

    updateOrganizationDoubleOptIn(form) {
        return form.put('core/organization/double-opt-in').then((response) => {
            this._setOrganizationDoubleOptInUpdated(response.data);
            return response;
        });
    },

    updateOrganizationCallStatus: (data) => {
        this._setOrganizationCallStatusUpdated(data);

        return baseApi
            .post('core/organization/call-status', data)
            .then((response) => {
                this._setOrganizationCallStatusUpdated(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationSettings(form) {
        return form.patch('core/organization/settings').then((response) => {
            this._setOrganizationSettingsUpdated(response.data);

            return response;
        });
    },

    organizationUpdate(organization) {
        this._organization = {
            ...this._organization,
            ...organization,
        };
    },

    renameOrganization({ form }) {
        return form.post(`core/agency/clients/${form.id}`).then((response) => {
            useClientsStore().setClientUpdated(response.data);
        });
    },

    updatePhoneCheckr(data) {
        this._setOrganizationUpdatePhoneCheckr(data);
    },

    updateOrganizationPhoneCheckerDisable() {
        return baseApi.post('core/organization/phone-checker/disable').then((response) => {
            return response.data;
        });
    },

    updateOrganizationPhoneCheckerEnable({ data }) {
        return baseApi.post('core/organization/phone-checker/enable', data).then((response) => {
            return response.data;
        });
    },

    deleteOrganizationFallbackInbox({ form }) {
        return form.delete('core/organization/fallback-inbox').then((response) => {
            return response.data;
        });
    },

    updateOrganizationSetFallbackInboxById({ form, id }) {
        return form.post(`core/organization/set-fallback-inbox/${id}`).then((response) => {
            this._setOrganizationUpdateFallbackId(response.data);

            return response.data;
        });
    },

    updateOrganizationInvitesByIdResend({ id }) {
        return baseApi.post(`core/organization/invites/${id}/resend`).then((response) => {
            return response.data;
        });
    },

    updateOrganizationAutoCloseConversationsStatus: (data) => {
        return baseApi
            .post('core/organization/auto-close-setting', data)
            .then((response) => {
                this._setOrganizationAutoCloseConversationsStatusUpdated(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationAutoCloseUnsubscribeStatus: (data) => {
        return baseApi
            .post('core/organization/auto-close-unsubscribe', data)
            .then((response) => {
                this._setOrganizationAutoCloseUnsubscribeStatusUpdated(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationConversationHistoryStatus: (data) => {
        return baseApi
            .post('core/organization/conversation-history-settings', data)
            .then((response) => {
                this._setOrganizationUseGlobalConversationHistory(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationExtendedInternationalTextingStatus: (data) => {
        return baseApi
            .post('core/organization/extended-international-texting-settings', data)
            .then((response) => {
                this._setOrganizationExtendedInternationalTexting(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationAutoCloseDays: (data) => {
        return baseApi
            .post('core/organization/auto-close-days', data)
            .then((response) => {
                this._setOrganizationAutoCloseDaysUpdated(data);
                return response;
            })
            .catch((e) => e);
    },

    updateOrganizationUpdateMessagingCanadaOnly: (data) => {
        return baseApi.post('core/organization/update-messaging-canada-only', data).catch((e) => e);
    },

    getBillingPreviewCustomDomainMicroservice(data) {
        return baseApi.get('billing/preview/custom-domain', data).then((response) => {
            return response.data;
        });
    },

    getOrganization() {
        return baseApi.get('core/organization/current').then((response) => {
            this._setOrganization(response.data);

            return response.data;
        });
    },

    _setOrganization(organization) {
        this._organization = organization;
        this._custom_fields_list = organization.custom_fields_list;
        this._broadcast_limits = organization.broadcast_limits;

        useAuth2fStore()._setOrganization(organization);
        useFilterSegmentStore()._setOrganization(organization);
        useNumberStore()._setOrganization(organization);
    },

    getUsersFeatureFlags() {
        return baseApi.get('core/users/feature-flags').then((response) => {
            const feature_flags = response.data;

            this._setUsersFeatureFlags(feature_flags);

            return feature_flags;
        });
    },

    _setUsersFeatureFlags(data) {
        this._feature_flags = data || {};
    },

    getOrganizationSettings() {
        return baseApi.get('core/organization/settings').then((response) => {
            this._setOrganizationSettings(response.data);

            return response.data;
        });
    },

    _setOrganizationSettings(organizationSettings) {
        this._settings = organizationSettings || [];
    },

    _setOrganizationState(data) {
        for (let key in data) {
            this[`_${key}`] = data[key];
        }
    },

    loadMultiOrganizationsList() {
        this._setOrganizationState({ isLoadingMultiOrganizations: true });

        return baseApi
            .get('/core/organization/multiorganizations/list', {
                params: { sortOrder: 'asc' },
            })
            .then((response) => {
                this._setMultiOrganizationsListLoaded(response.data.data);

                return response.data.data;
            })
            .catch((e) => {
                return e;
            })
            .finally(() => {
                this._setOrganizationState({ isLoadingMultiOrganizations: false });
            });
    },

    updateOrganizationCallViaAircall: (data) => {
        return baseApi
            .post('core/organization/call-settings/call-via-aircall', data)
            .then((response) => {
                this._setOrganizationCallViaAircallUpdated(response.data);

                return response.data;
            })
            .catch((e) => e);
    },

    updateOrganizationCallAutoRecord: (data) => {
        return baseApi
            .post('core/organization/call-settings/call-auto-record', data)
            .then((response) => {
                this._setOrganizationCallAutoRecordUpdated(response.data);

                return response.data;
            })
            .catch((e) => e);
    },

    resendTwoFaVerification() {
        return baseApi
            .post('core/compliance/services/brand/two-fa-verification/resend')
            .then((response) => {
                if (response.status === 204) {
                    useToastStore().showToast({
                        type: 'success',
                        message: '2FA confirmation email sent to your address.',
                    });
                }
                return response;
            })
            .catch((e) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: e.response.message,
                });

                return e.response;
            });
    },

    updateOrganizationIsNumbersGracePeriod: (status) => {
        this._setOrganizationIsNumbersGracePeriodUpdated(status);
    },

    _setNumberSelected() {
        ++this._organization.numbers_count;
    },

    _setInboxUpdateMediaVoiceMail({ media }) {
        if (Array.isArray(this._organization.voicemail_greetings)) {
            const index = this._organization.voicemail_greetings.findIndex(
                (item) => item.id === media.id
            );

            if (index === -1) {
                this._organization.voicemail_greetings.push(media);
            } else {
                Vue.set(this._organization.voicemail_greetings, index, media);
            }
        }
    },

    _setInboxUpdateMediaIvr({ inboxId, media }) {
        if (Array.isArray(this._organization.ivr_greetings)) {
            const index = this._organization.ivr_greetings.findIndex(
                (item) => item.id === media.id
            );

            if (index === -1) {
                this._organization.ivr_greetings.push(media);
            } else {
                Vue.set(this._organization.ivr_greetings, index, media);
            }
        }
    },

    _setInboxDeleteMediaIvr({ idGreeting }) {
        if (Array.isArray(this._organization.ivr_greetings)) {
            this._organization.ivr_greetings = this._organization.ivr_greetings.filter(
                (item) => item.id !== idGreeting
            );
        }
    },

    _setInboxDeleteMediaVoiceMail({ idGreeting }) {
        if (Array.isArray(this._organization.voicemail_greetings)) {
            this._organization.voicemail_greetings = this._organization.voicemail_greetings.filter(
                (item) => item.id !== idGreeting
            );
        }
    },

    _setUserFeatureFlagsLoaded(feature_flags) {
        this._feature_flags = feature_flags || {};
    },

    _setOrganizationSettingsLoaded(organizationSettings) {
        this._settings = organizationSettings || [];
    },

    _setOrganizationUpdateState(data) {
        for (let key in data) {
            this[`_${key}`] = data[key];
        }
    },

    _setOrganizationUpdateCustomFields({ type, data }) {
        const existField = this._custom_fields_list.find((item) => item.key === data.key);

        if (type === 'delete') {
            this._custom_fields_list = this._custom_fields_list.filter(
                (item) => item.key !== data.key
            );
        }

        if (type === 'create' && !existField) {
            this._custom_fields_list = [...this._custom_fields_list, data];
        }

        if (type === 'edit' && existField) {
            this._custom_fields_list = this._custom_fields_list.map((item) => {
                if (item.key === data.key) {
                    item = data;
                }

                return item;
            });

            this._custom_fields_list = this._custom_fields_list.filter(
                (item) => item.key !== data.key
            );
        }
    },

    _setOrganizationProfileUpdated(organization) {
        this._organization.name = organization.name;
    },

    _setOrganizationSettingsUpdated(updatedOption) {
        this._settings.find((option) => option.key === updatedOption.featureKey).settings[
            updatedOption.settingsKey
        ] = updatedOption.value;
    },

    _setOrganizationAccountBalanceUpdated(data) {
        this._organization.account_balance = parseFloat(data.account_balance);
        this._organization.account_credits = parseFloat(data.account_credits);
        this._organization.trial_credits = data.trial_credits;
    },

    _setOrganizationDoubleOptInUpdated(data) {
        this._organization.double_opt_in = data;
    },

    _setOrganizationCallStatusUpdated(data) {
        this._organization.organization_call = data;
    },

    _setOrganizationIsNumbersGracePeriodUpdated(status) {
        this._organization.is_numbers_grace_period = status;
    },

    _setOrganizationCallViaAircallUpdated(status) {
        this._organization.organization_call.call_via_aircall = status;
    },

    _setOrganizationCallAutoRecordUpdated(status) {
        this._organization.organization_call.call_auto_record = status;
    },

    _setOrganizationAutoCloseConversationsStatusUpdated(data) {
        this._organization.auto_close_setting = data.auto_close_setting;
    },

    _setOrganizationAutoCloseUnsubscribeStatusUpdated(data) {
        this._organization.auto_close_unsubscribe = data.auto_close_unsubscribe;
    },

    _setOrganizationAutoCloseDaysUpdated(data) {
        this._organization.auto_close_days = data.auto_close_days;
    },

    _setOrganizationUpdateFallbackId(data) {
        if (this._organization.fallback_team) {
            this._organization.fallback_team.id = data.fallback_team_id;
        }
        this._organization.organization_id = data.organization_id;
    },

    _setOrganizationUpdatePhoneCheckr(data) {
        this._organization.phone_checker = data;
        return this;
    },

    _setMultiOrganizationsListLoaded(data) {
        this._multiOrganizationsList = data;
    },

    _setOrganizationUseGlobalConversationHistory(data) {
        this._organization.use_global_conversation_history = data.use_global_conversation_history;
    },

    _setOrganizationExtendedInternationalTexting(data) {
        this._organization.extended_international_texting = data.extended_international_texting;
    },
};

export default {
    state,
    getters,
    actions,
};
